import { useMemo } from "react";
import useTranslations from "./useTranslations";

// Hook for mapping content across all pages
export const useContentMap = () => {
  const { t, lang } = useTranslations();

  // Memoized content mapping for better performance
  const contentMap = useMemo(() => {
    return {
      // Common content used across multiple pages
      common: {
        loading: t("common.loading"),
        error: t("common.error"),
        save: t("common.save"),
        cancel: t("common.cancel"),
        confirm: t("common.confirm"),
        back: t("common.back"),
        next: t("common.next"),
        previous: t("common.previous"),
        close: t("common.close"),
        search: t("common.search"),
        select: t("common.select"),
        edit: t("common.edit"),
        delete: t("common.delete"),
        add: t("common.add"),
        view: t("common.view"),
        submit: t("common.submit"),
        reset: t("common.reset"),
        refresh: t("common.refresh"),
        success: t("common.success"),
        failed: t("common.failed"),
        warning: t("common.warning"),
        info: t("common.info"),
      },

      // Navigation content
      navigation: {
        home: t("navigation.home"),
        profile: t("navigation.profile"),
        settings: t("navigation.settings"),
        language: t("navigation.language"),
        logout: t("navigation.logout"),
      },

      // Profile page content
      profile: {
        title: t("profile.title"),
        viewProfile: t("profile.viewProfile"),
        myPoints: t("profile.myPoints"),
        myCoupons: t("profile.myCoupons"),
        editProfile: t("profile.editProfile"),
        personalInfo: t("profile.personalInfo"),
        contactInfo: t("profile.contactInfo"),
        termAndConditions: t("profile.termAndConditions"),
        privacyPolicy: t("profile.privacyPolicy"),
        contactUs: t("profile.contactUs"),
        tpdWebsite: t("profile.tpdWebsite"),
        contactCenter: t("profile.contactCenter"),
      },

      // Profile Detail page content
      profileDetail: {
        title: t("profileDetail.title"),
        edit: t("profileDetail.edit"),
        fields: {
          name: t("profileDetail.fields.name"),
          fullName: t("profileDetail.fields.fullName"),
          phoneNumber: t("profileDetail.fields.phoneNumber"),
          idCard: t("profileDetail.fields.idCard"),
          email: t("profileDetail.fields.email"),
          birthDate: t("profileDetail.fields.birthDate"),
          gender: t("profileDetail.fields.gender"),
        },
        genderOptions: {
          male: t("profileDetail.genderOptions.male"),
          female: t("profileDetail.genderOptions.female"),
          notSpecified: t("profileDetail.genderOptions.notSpecified"),
        },
      },

      // Profile Edit page content
      profileEdit: {
        title: t("profileEdit.title"),
        save: t("profileEdit.save"),
        cancel: t("profileEdit.cancel"),
        fields: {
          fullName: t("profileEdit.fields.fullName"),
          phoneNumber: t("profileEdit.fields.phoneNumber"),
          idCard: t("profileEdit.fields.idCard"),
          email: t("profileEdit.fields.email"),
          birthDate: t("profileEdit.fields.birthDate"),
          gender: t("profileEdit.fields.gender"),
        },
        placeholders: {
          fullName: t("profileEdit.placeholders.fullName"),
          phoneNumber: t("profileEdit.placeholders.phoneNumber"),
          idCard: t("profileEdit.placeholders.idCard"),
          email: t("profileEdit.placeholders.email"),
        },
        validation: {
          required: {
            fullName: t("profileEdit.validation.required.fullName"),
            phoneNumber: t("profileEdit.validation.required.phoneNumber"),
            idCard: t("profileEdit.validation.required.idCard"),
            email: t("profileEdit.validation.required.email"),
            birthDate: t("profileEdit.validation.required.birthDate"),
            gender: t("profileEdit.validation.required.gender"),
          },
          format: {
            phoneNumber: t("profileEdit.validation.format.phoneNumber"),
            idCard: t("profileEdit.validation.format.idCard"),
            email: t("profileEdit.validation.format.email"),
          },
          exists: {
            phoneNumber: t("profileEdit.validation.exists.phoneNumber"),
            idCard: t("profileEdit.validation.exists.idCard"),
            email: t("profileEdit.validation.exists.email"),
          },
        },
        genderOptions: {
          male: t("profileEdit.genderOptions.male"),
          female: t("profileEdit.genderOptions.female"),
          notSpecified: t("profileEdit.genderOptions.notSpecified"),
        },
      },

      // Language page content
      language: {
        title: t("language.title"),
        description: t("language.description"),
        thai: t("language.thai"),
        english: t("language.english"),
        currentLanguage: t("language.currentLanguage"),
        selectLanguage: t("language.selectLanguage"),
        confirmChange: t("language.confirmChange"),
        changeSuccess: t("language.changeSuccess"),
      },

      // Member page content
      member: {
        title: t("member.title"),
        welcome: t("member.welcome"),
        viewProfile: t("member.viewProfile"),
        myPoints: t("member.myPoints"),
        myCode: t("member.myCode"),
        pointsDescription: t("member.pointsDescription"),
        noMemberList: t("member.noMemberList"),
        visitWebsite: t("member.visitWebsite"),
        ourWebsite: t("member.ourWebsite"),
        addMember: t("member.addMember"),
        memberPaypoint: t("member.memberPaypoint"),
        highlight: t("member.highlight"),
        codes: t("member.codes"),
        noPartnerData: t("member.noPartnerData"),
        loadingMemberData: t("member.loadingMemberData"),
        memberList: t("member.memberList"),
        points: t("member.points"),
      },
    };
  }, [t, lang]);

  return contentMap;
};

// Hook for getting page-specific content
export const usePageContent = (pageName) => {
  const contentMap = useContentMap();
  return contentMap[pageName] || {};
};

// Hook for dynamic content mapping
export const useDynamicContent = () => {
  const { t } = useTranslations();

  return {
    t, // Direct access to translation function for dynamic keys
    formatMessage: (key, params = {}) => {
      let message = t(key);
      Object.keys(params).forEach((param) => {
        message = message.replace(`{${param}}`, params[param]);
      });
      return message;
    },
  };
};

export default useContentMap;
